import { Badge } from '@mantine/core';
import { useIntl } from 'react-intl';

import { handlingUnitSectionMessages } from './handling-unit-section-messages';
import styles from './HandlingUnitSection.module.css';
import { convertDimensions } from '../../../../utils/dimensions';
import { unitMessages } from '../../../../utils/unit-messages';
import { convertWeight } from '../../../../utils/weight-utils';
import { handlingUnitsStepMessages } from '../../../freight-booking-wizard/components/handling-units-step/handling-units-step-messages';
import {
  hazardClassesToDotKey,
  PACKING_GROUP_OPTIONS,
} from '../../../freight-booking-wizard/components/handling-units-step/hazmat-options';
import { FREIGHT_CLASS_OPTIONS } from '../../../freight-browser/components/freight-form/freight-class-options';
import { DetailSection } from '../detail-section/DetailSection';
import { MeasurementCell } from '../measurement-cell/MeasurementCell';

import type { DimensionUnit } from '../../../../types/dimensions';
import type { FreightHazmatInfo, LtlCommodity, LtlHandlingUnit, LtlShipment } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';
import type { MessageDescriptor } from 'react-intl';

type HandlingUnitSectionProps = {
  shipment: LtlShipment;
  displayDimensionUnit: DimensionUnit;
  displayWeightUnit: WeightUnit;
};

const DIMENSION_UNIT_ABBREVIATION: Record<DimensionUnit, MessageDescriptor> = {
  Inches: unitMessages.inches,
  Centimeters: unitMessages.centimeters,
};

const WEIGHT_UNIT_ABBREVIATION: Record<WeightUnit, MessageDescriptor> = {
  Pounds: unitMessages.pounds,
  Ounces: unitMessages.ounces,
  Kilograms: unitMessages.kilograms,
  Grams: unitMessages.grams,
};

/** Maps a stored freight class enum (e.g. `_77_5`) to its display label (`77.5`). */
const freightClassLabel = (freightClass: LtlCommodity['freightClass']): string =>
  FREIGHT_CLASS_OPTIONS.find((option) => option.value === freightClass)?.label ?? '';

/**
 * Left-column Handling Units card. Renders each handling unit's dimensions and
 * stackability, plus a gray inner card per commodity with its class, converted
 * total weight, NMFC code and piece details. Store-agnostic — props only.
 */
export const HandlingUnitSection = ({ shipment, displayDimensionUnit, displayWeightUnit }: HandlingUnitSectionProps) => {
  const intl = useIntl();

  const dimensionUnitLabel = intl.formatMessage(DIMENSION_UNIT_ABBREVIATION[displayDimensionUnit]);
  const weightUnitLabel = intl.formatMessage(WEIGHT_UNIT_ABBREVIATION[displayWeightUnit]);

  const formatDimension = (value: number) =>
    intl.formatMessage(handlingUnitSectionMessages.dimensionValue, { value, unit: dimensionUnitLabel });

  const packingGroupLabel = (packingGroup: FreightHazmatInfo['packingGroup']) =>
    packingGroup === 'NoPackagingGroup'
      ? intl.formatMessage(handlingUnitsStepMessages.hazmatNoPackagingGroupOption)
      : PACKING_GROUP_OPTIONS.find((option) => option.value === packingGroup)?.label;

  const renderHazmatInfo = (hazmatInfo: FreightHazmatInfo | null) => (
    <div className={styles.hazmat} data-testid='commodity-hazmat'>
      <span className={styles.hazmatTitle}>{intl.formatMessage(handlingUnitSectionMessages.hazmatTitle)}</span>
      <div className={styles.hazmatGrid}>
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatIdNumberLabel)}
          value={hazmatInfo?.identificationNumber}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatProperShippingNameLabel)}
          value={hazmatInfo?.properShippingName}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatHazardClassLabel)}
          value={hazardClassesToDotKey(hazmatInfo?.hazardClass)}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatPackingGroupLabel)}
          value={packingGroupLabel(hazmatInfo?.packingGroup ?? null)}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatErgReferenceLabel)}
          value={hazmatInfo?.emergencyResponseReference}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatFlashpointLabel)}
          value={hazmatInfo?.flashpointTemperatureF}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatEmergencyContactNameLabel)}
          value={hazmatInfo?.emergencyContactName}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatEmergencyPhoneLabel)}
          value={hazmatInfo?.emergencyPhone}
        />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitsStepMessages.hazmatAdditionalDetailsLabel)}
          value={hazmatInfo?.additionalDetails}
        />
      </div>
    </div>
  );

  const renderCommodity = (commodity: LtlCommodity, index: number) => {
    // Commodity weights are stored in each commodity's own weightUnit (the wizard
    // gotcha); convert to the seller's display unit before showing.
    const convertedWeight = convertWeight(commodity.weight, commodity.weightUnit ?? displayWeightUnit, displayWeightUnit);
    const roundedWeight = Math.round(convertedWeight * 100) / 100;

    return (
      <div className={styles.commodityCard} key={commodity.id}>
        <span className={styles.commodityLabel}>
          {intl.formatMessage(handlingUnitSectionMessages.commodityLabel, { number: index + 1 })}
          {commodity.isHazmat && (
            <Badge color='alert' ml='xs' size='sm' variant='filled'>
              {intl.formatMessage(handlingUnitSectionMessages.hazmatBadge)}
            </Badge>
          )}
        </span>
        <div className={styles.measurementRow}>
          <MeasurementCell
            label={intl.formatMessage(handlingUnitSectionMessages.totalWeight)}
            value={intl.formatMessage(handlingUnitSectionMessages.weightValue, { value: roundedWeight, unit: weightUnitLabel })}
          />
          <MeasurementCell
            label={intl.formatMessage(handlingUnitSectionMessages.freightClass)}
            value={freightClassLabel(commodity.freightClass)}
          />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.nmfcCode)} value={commodity.nmfcCode} />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.pieceType)} value={commodity.pieceType} />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.pieceQuantity)} value={commodity.numberOfPieces} />
        </div>
        {commodity.description && (
          <div className={styles.commodityDescription}>
            <span className={styles.commodityDescriptionLabel}>{intl.formatMessage(handlingUnitSectionMessages.commodityDescription)}</span>
            <span className={styles.commodityDescriptionValue}>{commodity.description}</span>
          </div>
        )}
        {commodity.isHazmat && renderHazmatInfo(commodity.hazmatInfo)}
      </div>
    );
  };

  const renderUnit = (unit: LtlHandlingUnit, index: number) => {
    // Dimensions are stored in the unit's own dimensionUnit (which may differ from the
    // seller's current display preference); convert before rendering, same pattern as weight.
    // Guard against invalid stored values (e.g. 'Unknown') that aren't in our DimensionUnit
    // union — only accept the two known values, fall back to displayDimensionUnit otherwise.
    const storedDimensionUnit: DimensionUnit =
      unit.dimensionUnit === 'Inches' || unit.dimensionUnit === 'Centimeters' ? unit.dimensionUnit : displayDimensionUnit;
    const dims = convertDimensions(
      { length: unit.length, width: unit.width, height: unit.height, unit: storedDimensionUnit },
      displayDimensionUnit,
    );

    return (
      <div className={styles.unit} key={unit.id}>
        <span className={styles.unitLabel}>
          {intl.formatMessage(handlingUnitSectionMessages.unitLabel, { number: index + 1, packaging: unit.packagingType })}
        </span>
        <div className={styles.measurementRow}>
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.quantity)} value={unit.quantity} />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.width)} value={formatDimension(dims.width)} />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.length)} value={formatDimension(dims.length)} />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.height)} value={formatDimension(dims.height)} />
          <MeasurementCell
            label={intl.formatMessage(handlingUnitSectionMessages.stackable)}
            value={intl.formatMessage(
              unit.isStackable ? handlingUnitSectionMessages.stackableYes : handlingUnitSectionMessages.stackableNo,
            )}
          />
        </div>
        <div className={styles.commodities}>{unit.commodities.map(renderCommodity)}</div>
      </div>
    );
  };

  return (
    <DetailSection testId='handling-unit-section' title={intl.formatMessage(handlingUnitSectionMessages.sectionTitle)}>
      <div className={styles.units}>{shipment.handlingUnits.map(renderUnit)}</div>
    </DetailSection>
  );
};
